import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./conversation.reply.scss";
import { Select, Skeleton, Form, message } from "antd";
import { TrashIcon } from "@Assets/icons";
import {
  CannedAnswerChannelEnum,
  CannedAnswerTypeEnum,
  ChannelEnum,
  DraftChannelEnum,
  IPreviewCannedAnswer,
  IPreviewForm,
  IPreviewMailAccount,
  IPreviewSignature,
  ISharedAiRequest,
  ISharedDraft,
  ISharedFormSubmission,
  ISharedFullConversation,
  ISharedQueue,
  Nullable,
  SignatureTypeEnum,
  Undefinable,
} from "atlas-shared";
import {
  deleteDraftAttachment,
  downloadDraftAttachment,
  fetchAiRequest,
  patchDraft,
  sendDraft,
} from "@Api";
import { AlertError, Confirm, filterRelated, RestRequest, TypeModal } from "@Utils";
import { useCannedAnswers, useMailAccounts, useQueues, useSignatures } from "@Hooks";
import { FormHtmlEditor, FormSelect, IFormElementHtml } from "atlas-form";
import {
  Button,
  ConversationFormTablePreview,
  ConversationMessageFormSubmission,
  Icon,
} from "@Components";
import { ConversationMessageReplyTimer } from "./conversation.message.reply.timer";
import FroalaEditor from "froala-editor";
import { IAuth, useAppDispatch } from "@Store";
import { useCreateTextMessage } from "./useCreateTextMessage";
import { EditorFroala } from "@Components/editor-froala";
import { hashOptions } from "@Components/dashboard/create-conversation/constants/hashOptions";
import { useFeatureFlag } from "@Hooks/useFeatureFlag";
import { ContactSearchSelect } from "@Components/dashboard/create-conversation/components/ContactSearchSelect";
import { htmlToPlainTextWithLineBreaks, tryCatch } from "@Utils";

interface IProps {
  draft: ISharedDraft;
  conversation: ISharedFullConversation;
  conversationForm: Undefinable<IPreviewForm>;
  auth: IAuth;
  onDelete?: () => Promise<any>;
  fetchTextMessages?: () => void;
}

export const ConversationReply = React.memo(
  ({
    conversation,
    onDelete,
    draft: _draft,
    auth,
    conversationForm,
    fetchTextMessages,
  }: IProps) => {
    const { t } = useTranslation();
    const { Option } = Select;
    const [draft, _setDraft] = useState<ISharedDraft>(_draft);
    const signatures = useSignatures();
    const mail_accounts = useMailAccounts();
    const cannedAnswers = useCannedAnswers();
    const queues = useQueues();
    const [queue, setQueue] = useState<Undefinable<ISharedQueue>>();
    const [save, setSave] = useState<Nullable<number>>(null);
    const [sent, setSent] = useState<boolean>(false);
    const [displayEditReply, setDisplayEditReply] = useState<boolean>(false);
    const [editReply, setEditReply] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);
    const [formSubmissionPreviewImport, setFormSubmissionPreviewImport] =
      useState<Undefinable<React.ReactElement>>();
    const [conversationFormPreviewImport, setConversationFormPreviewImport] =
      useState<Undefinable<React.ReactElement>>();
    const [signature, setSignature] = useState<Undefinable<IPreviewSignature>>();
    const [ai_request, set_ai_request] = useState<Nullable<ISharedAiRequest>>(null);
    const dispatch = useAppDispatch();
    const useTextMessageVariables = useFeatureFlag("text.message.variables", false);
    const useLoadCannedAnswersInBackground = useFeatureFlag(
      "editor.background_loading_canned_answers",
      false
    );

    const [showCC, setShowCC] = useState(!!draft.reply_to_cc?.length);
    const [showBCC, setShowBCC] = useState(!!draft.reply_to_bcc?.length);
    const formRef = useRef<any>(null);

    const {
      textMessage,
      updateTextMessage,
      sendMessage: sendTextMessage,
      textMessageSources,
      MAX_TEXT_MESSAGE_LENGTH,
      MIN_TEXT_MESSAGE_LENGTH,
    } = useCreateTextMessage(conversation, queue, onDelete);

    const handleSendTextMessage = useCallback(async () => {
      setSending(true);
      const { error: sendError } = await tryCatch(sendTextMessage());
      if (!sendError && fetchTextMessages) {
        await tryCatch(Promise.resolve(fetchTextMessages()));
      }
      if (sendError) {
        message.error(t("FAILED_TO_SEND_MESSAGE"));
        console.error("Failed to send text message:", sendError);
      }
      setSending(false);
    }, [sendTextMessage, fetchTextMessages]);

    useEffect(() => {
      if (draft.signature_id != signature?.id)
        setSignature(draft.signature_id ? signatures.dict[draft.signature_id] : undefined);
    }, [draft.signature_id, signatures]);

    useEffect(() => {
      if (queues.loaded && conversation.queue_id)
        setQueue(queues.dict[conversation.queue_id] as ISharedQueue);
    }, [queues, conversation.queue_id]);

    useEffect(() => {
      if (draft.ai_request_id && !ai_request)
        fetchAiRequest(draft.organization_id, draft.ai_request_id).then((ai_request) =>
          set_ai_request(ai_request)
        );
    }, [draft.ai_request_id]);

    useEffect(() => {
      if (!draft) return;

      _setDraft({ ...draft, attachments: _draft.attachments });
    }, [_draft.attachments]);

    const _patchDraft = useCallback((draft: ISharedDraft) => {
      const { id, organization_id, created_at, updated_at, ...patch } = draft;

      return patchDraft(id, patch);
    }, []);

    const handleTextMessageChange = (value: string) => {
      const plainText = htmlToPlainTextWithLineBreaks(value);
      updateTextMessage("text", plainText);
    };

    const importFormSubmission = useCallback(
      (editor: FroalaEditor) => {
        if (conversation.messages?.at(-1)?.channel !== ChannelEnum.Form) return;

        if (!formSubmissionPreviewImport) {
          setFormSubmissionPreviewImport(
            <span className="table-import" id="table-import-form-submission">
              <div className="form-submission-content">
                <ConversationMessageFormSubmission
                  t={t}
                  auth={auth}
                  message={conversation.messages.at(-1) as ISharedFormSubmission}
                  conversation={conversation}
                  dispatch={dispatch}
                  isStatic={true}
                />
              </div>
            </span>
          );
          setTimeout(() => {
            editor.html.insert(
              document.getElementById("table-import-form-submission")?.innerHTML ||
                "content not loaded"
            );
            setFormSubmissionPreviewImport(undefined);
          }, 500);
        }
      },
      [conversation, formSubmissionPreviewImport]
    );

    const importConversationForm = useCallback(
      (editor: FroalaEditor) => {
        if (!conversationFormPreviewImport) {
          setConversationFormPreviewImport(
            <span className="table-import" id="table-import-conversation-form">
              <div className="form-submission-content">
                <ConversationFormTablePreview conversation={conversation} form={conversationForm} />
              </div>
            </span>
          );
          setTimeout(() => {
            editor.html.insert(
              document.getElementById("table-import-conversation-form")?.innerHTML ||
                "content not loaded"
            );
            setConversationFormPreviewImport(undefined);
          }, 500);
        }
      },
      [conversation, conversationForm]
    );

    const saveDraft = useCallback(() => {
      return _patchDraft(draft)
        .then((saved) => {
          _setDraft({ ...draft, updated_at: saved.updated_at });
        })
        .catch((error) => {
          AlertError(t, { title: t("ERROR"), content: error.toString() });
        });
    }, [draft, t, _patchDraft]);

    const sendMessage = useCallback(() => {
      return new Promise((resolve) => {
        setSending(true);
        saveDraft()
          .then((_) => {
            sendDraft(draft.id)
              .then(() => {
                setSent(true);
                setSending(false);
              })
              .catch((e) => {
                AlertError(t, { content: e });
                setSending(false);
              });
          })
          .finally(() => resolve(true));
      });
    }, [draft, t, _patchDraft, saveDraft, textMessage]);

    useEffect(() => {
      if (!save || sent) return;

      const timer = setTimeout(() => {
        saveDraft();
        setSave(null);
      }, 2000);

      return () => clearTimeout(timer);
    }, [save, sent, setSave, saveDraft]);

    const setDraft = useCallback(
      (partial_draft: Partial<ISharedDraft>) => {
        const _draft = { ...draft, ...partial_draft };

        _setDraft(_draft);
        setSave(Math.random());
      },
      [draft]
    );

    if (sending || !conversation?.messages)
      return (
        <div style={{ marginBottom: 20, opacity: 0.5 }}>
          <Skeleton avatar active paragraph={{ rows: 3 }} />
        </div>
      );

    return (
      <>
        {formSubmissionPreviewImport}
        {conversationFormPreviewImport}
        <div className={"reply-wrapper"}>
          {ai_request && (
            <div className="ai-request">
              <span className="confidence">{t("AI_RESPONSE_CONFIDENCE")}</span>
              <span className="score">{Math.round((ai_request.confidence / 10) * 100)}%</span>
            </div>
          )}
          {draft.channel === DraftChannelEnum.Mail && (
            <div className={"subject-container"}>
              <span className={"text"}>{t("SUBJECT")}</span>
              <input
                className="subject-input"
                onChange={(e) => setDraft({ subject: e.target.value })}
                defaultValue={draft.subject || ""}
              />
            </div>
          )}
          <div className="reply-contents">
            <div className="reply-header">
              <div className="h-left">
                {draft.channel === DraftChannelEnum.Mail && (
                  <>
                    <div className="section">
                      <label style={{ width: 38 }}>{t("FROM")}</label>
                      <FormSelect
                        onChange={(v) => setDraft({ account_id: v || null })}
                        value={draft.account_id || undefined}
                        className="bold"
                        options={filterRelated(
                          draft.organization_id,
                          "mail_account",
                          "conversation",
                          mail_accounts.mail_accounts,
                          true,
                          (mail_account: IPreviewMailAccount) =>
                            (queue?.__outbound_mail_accounts || []).includes(mail_account.id)
                        )}
                      />
                    </div>
                    <Form
                      className="to-form"
                      layout="vertical"
                      initialValues={{
                        reply_to_address: draft.reply_to_address,
                        reply_to_cc: draft.reply_to_cc,
                        reply_to_bcc: draft.reply_to_bcc,
                      }}
                      ref={formRef}
                      onValuesChange={(changed, all) => {
                        if (changed.reply_to_address !== undefined) {
                          setDraft({ reply_to_address: changed.reply_to_address });
                        }
                        if (changed.reply_to_cc !== undefined) {
                          setDraft({ reply_to_cc: changed.reply_to_cc });
                        }
                        if (changed.reply_to_bcc !== undefined) {
                          setDraft({ reply_to_bcc: changed.reply_to_bcc });
                        }
                      }}
                    >
                      <div style={{ position: "relative" }}>
                        <div className="section">
                          <label style={{ marginLeft: 6 }}>{t("TO")}</label>
                          <div className="to-row">
                            <ContactSearchSelect
                              name="reply_to_address"
                              valueKey="contact_email"
                              required
                              mode={undefined}
                              organizationId={draft.organization_id}
                              selectedQueue={queue}
                              agentprofileId={auth.user.__agentprofiles?.[0]}
                              containerClassName="to-contact-select"
                              onContactSelect={(_, email) => {
                                setDraft({ reply_to_address: email });
                                formRef.current?.setFieldsValue({ reply_to_address: email });
                              }}
                            />
                            <Button
                              className="antd-default-styling cc-btn"
                              style={{
                                textDecoration: showCC ? "line-through" : undefined,
                              }}
                              onClick={() => {
                                setShowCC((prev) => {
                                  if (prev) {
                                    setDraft({ reply_to_cc: [] });
                                    formRef.current?.setFieldsValue({ reply_to_cc: [] });
                                  }
                                  return !prev;
                                });
                              }}
                              type="link"
                              size="middle"
                            >
                              CC
                            </Button>
                            <Button
                              className="antd-default-styling bcc-btn"
                              style={{
                                textDecoration: showBCC ? "line-through" : undefined,
                              }}
                              onClick={() => {
                                setShowBCC((prev) => {
                                  if (prev) {
                                    setDraft({ reply_to_bcc: [] });
                                    formRef.current?.setFieldsValue({ reply_to_bcc: [] });
                                  }
                                  return !prev;
                                });
                              }}
                              type="link"
                              size="middle"
                            >
                              BCC
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="section" style={{ marginTop: 6, marginLeft: 6 }}>
                        {showCC && (
                          <div className="section" style={{ width: "50%" }}>
                            <span style={{ marginRight: 4, fontSize: 10, width: "24px" }}>CC</span>
                            <ContactSearchSelect
                              name="reply_to_cc"
                              valueKey="contact_email"
                              required={false}
                              mode="tags"
                              organizationId={draft.organization_id}
                              selectedQueue={queue}
                              agentprofileId={auth.user.__agentprofiles?.[0]}
                              style={{ width: "100%" }}
                              containerClassName="cc-bcc-container"
                              onContactSelect={(_, email) => {
                                const cc = Array.isArray(draft.reply_to_cc)
                                  ? draft.reply_to_cc
                                  : [];
                                if (!cc.includes(email)) {
                                  const newCC = [...cc, email];
                                  setDraft({ reply_to_cc: newCC });
                                  formRef.current?.setFieldsValue({ reply_to_cc: newCC });
                                }
                              }}
                            />
                          </div>
                        )}
                        {showBCC && (
                          <div className="section" style={{ width: "50%" }}>
                            <span style={{ marginRight: 6, fontSize: 10, width: "30px" }}>BCC</span>
                            <ContactSearchSelect
                              name="reply_to_bcc"
                              valueKey="contact_email"
                              required={false}
                              mode="tags"
                              organizationId={draft.organization_id}
                              selectedQueue={queue}
                              agentprofileId={auth.user.__agentprofiles?.[0]}
                              style={{ width: "100%" }}
                              containerClassName="cc-bcc-container"
                              onContactSelect={(_, email) => {
                                const bcc = Array.isArray(draft.reply_to_bcc)
                                  ? draft.reply_to_bcc
                                  : [];
                                if (!bcc.includes(email)) {
                                  const newBCC = [...bcc, email];
                                  setDraft({ reply_to_bcc: newBCC });
                                  formRef.current?.setFieldsValue({ reply_to_bcc: newBCC });
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </Form>
                  </>
                )}
                {draft.channel === DraftChannelEnum.Sms && (
                  <>
                    <div className="section">
                      <label>{t("SOURCE")}</label>
                      <Select
                        onChange={(v: any) => {
                          updateTextMessage("sourceId", v.value);
                        }}
                        value={
                          textMessage?.source?.id && textMessageSources.length > 0
                            ? {
                                value: textMessage.source.id,
                                label:
                                  textMessageSources.find(
                                    (source) =>
                                      String(source.value) === String(textMessage.source?.id)
                                  )?.label || `Source ${textMessage.source?.id}`,
                              }
                            : undefined
                        }
                        className="bold"
                        options={textMessageSources}
                        labelInValue
                        placeholder={t("SELECT_SOURCE")}
                      />
                    </div>
                    <div className="section">
                      <label>{t("TO")}</label>
                      <Select
                        value={
                          textMessage?.number ?? conversation.contact?.phones?.[0] ?? undefined
                        }
                        onChange={(v) => updateTextMessage("number", v)}
                      >
                        {conversation.contact?.phones?.map((phone) => (
                          <Option key={phone} value={phone}>
                            {phone}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  </>
                )}
              </div>
              <div className="h-right">
                <Icon
                  onClick={() =>
                    Confirm(t, {
                      onOk: (modal: TypeModal) => onDelete?.().then((deleted) => modal.destroy()),
                      title: t("PERMANENTLY_DELETE_DRAFT"),
                    })
                  }
                  icon={TrashIcon}
                  iconProps={{ size: 17 }}
                  tooltip={{ title: t("DELETE_DRAFT") }}
                />
              </div>
            </div>
            {(useLoadCannedAnswersInBackground || cannedAnswers.loaded) &&
              draft.channel !== DraftChannelEnum.Sms && (
                <FormHtmlEditor
                  onChange={(value) => {
                    setDraft({ body: value });
                  }}
                  showLoadingSpinner={
                    useLoadCannedAnswersInBackground &&
                    draft.channel !== DraftChannelEnum.InternalNote
                  }
                  isCannedAnswersLoaded={cannedAnswers.loaded}
                  setDisplayEditReply={setDisplayEditReply}
                  value={draft.body}
                  hashOptions={
                    useTextMessageVariables ? ["contact", "conversation", "agent"] : undefined
                  }
                  atOptions={["user"]}
                  cannedAnswers={
                    draft.channel === DraftChannelEnum.InternalNote
                      ? []
                      : cannedAnswers.canned_answers.filter(
                          (canned_answer: IPreviewCannedAnswer) =>
                            !canned_answer.deleted_at &&
                            canned_answer.type == CannedAnswerTypeEnum.Agent &&
                            canned_answer.channel === (draft.channel as any) &&
                            (canned_answer.global ||
                              (queue?.__canned_answers || []).includes(canned_answer.id))
                        )
                  }
                  file_upload={{
                    attachments: draft.attachments || [],
                    action: RestRequest.getFullSrc(`/draft/${draft.id}/attachment`),
                    headers: RestRequest.getHeaders(),
                    onDelete: (uid: number) => deleteDraftAttachment(draft.id, uid),
                    onDownload: (uid: number, name: string) =>
                      downloadDraftAttachment(draft.id, uid, name),
                    onDownloadUri: (index: number) => `draft/${draft.id}/attachment/${index}`,
                    draft_id: draft.id,
                  }}
                  tableImport={
                    [
                      conversation.messages?.at(-1)?.channel === ChannelEnum.Form && {
                        key: "form_submission",
                        title: t("IMPORT_FORM_SUBMISSION"),
                        callback: importFormSubmission,
                      },
                      conversationForm?.id && {
                        key: "conversation_form",
                        title: t("IMPORT_CONVERSATION_FORM"),
                        callback: importConversationForm,
                      },
                    ].filter(Boolean) as IFormElementHtml["tableImport"]
                  }
                />
              )}
            {draft.channel === DraftChannelEnum.Sms && (
              <>
                <div className="section">
                  <EditorFroala
                    disableRichText={true}
                    maxLength={MAX_TEXT_MESSAGE_LENGTH}
                    placeholder={t("MESSAGE")}
                    onChangeTrigger={handleTextMessageChange}
                    canned_answers={cannedAnswers.canned_answers.filter(
                      (canned_answer: IPreviewCannedAnswer) =>
                        !canned_answer.deleted_at &&
                        canned_answer.type == CannedAnswerTypeEnum.Agent &&
                        canned_answer.channel === CannedAnswerChannelEnum.Sms &&
                        (canned_answer.global ||
                          (queue?.__canned_answers || []).includes(canned_answer.id))
                    )}
                    editorToggle={null}
                    hashOptions={useTextMessageVariables ? hashOptions : undefined}
                  />
                </div>
                <div className="reply-footer">
                  {textMessage?.text && textMessage?.text?.length > MAX_TEXT_MESSAGE_LENGTH && (
                    <div style={{ color: "red", marginRight: "auto" }}>
                      Max {MAX_TEXT_MESSAGE_LENGTH}
                    </div>
                  )}

                  <Button
                    type="primary"
                    onClick={handleSendTextMessage}
                    disabled={
                      // Disabled if text is too long or too short
                      Boolean(textMessage?.text?.length) &&
                      ((textMessage?.text?.length ?? 0) < MIN_TEXT_MESSAGE_LENGTH ||
                        (textMessage?.text?.length ?? 0) > MAX_TEXT_MESSAGE_LENGTH ||
                        sending)
                    }
                  >
                    {t("SEND")}
                  </Button>
                </div>
              </>
            )}
            {draft.channel !== DraftChannelEnum.Sms && (
              <div className="reply-footer">
                {displayEditReply && draft.reply_body && (
                  <Button onClick={() => setEditReply(!editReply)} className="draft">
                    {t(editReply ? "HIDE_REPLY" : "EDIT_REPLY")}
                  </Button>
                )}
                {displayEditReply &&
                  !draft.reply_body &&
                  (draft.reply_to || "").startsWith(`${ChannelEnum.Form}-`) && (
                    <Button
                      onClick={() => {
                        setEditReply(!editReply);
                        setDraft({
                          reply_body:
                            '<div id="xm-mail-content"><blockquote type="cite"><p></p></blockquote></div>',
                        });
                      }}
                      className="draft"
                    >
                      {t("CREATE_REPLY")}
                    </Button>
                  )}
                {draft.channel !== DraftChannelEnum.InternalNote && (
                  <div className="signature">
                    <label>{t("SIGNATURE")}</label>
                    <FormSelect
                      onChange={(v) => setDraft({ signature_id: v || null })}
                      value={draft.signature_id || undefined}
                      className="bold"
                      options={filterRelated(
                        draft.organization_id,
                        "signature",
                        "conversation",
                        signatures.signatures,
                        true,
                        (signature: IPreviewSignature) =>
                          signature.type == SignatureTypeEnum.Agent &&
                          signature.channel === (draft.channel as any) &&
                          (signature.global ||
                            (queue?.__agent_signatures || []).includes(signature.id))
                      )}
                    />
                  </div>
                )}
                <div className="update-since">
                  <label>{t("LAST_SAVE")}</label>
                  <ConversationMessageReplyTimer t={t} since={draft.updated_at} />
                </div>
                <Button onClick={() => saveDraft()} className="draft">
                  {t("SAVE_DRAFT")}
                </Button>
                <Button type="primary" onClick={sendMessage} disabled={!draft.body}>
                  {t(
                    draft.channel === DraftChannelEnum.InternalNote
                      ? "CREATE_INTERNAL_NOTE"
                      : "SEND"
                  )}
                </Button>
              </div>
            )}
            {signature?.body && (
              <div className="preview-signature">
                <label title={t("SIGNATURE_PREVIEW")}>{t("SIGNATURE_PREVIEW")}</label>
                <div
                  className="preview-signature-body"
                  dangerouslySetInnerHTML={{ __html: signature.body }}
                ></div>
              </div>
            )}
            {displayEditReply && draft.reply_body && editReply && (
              <div className="reply-history">
                <FormHtmlEditor
                  showLoadingSpinner={
                    useLoadCannedAnswersInBackground &&
                    draft.channel !== DraftChannelEnum.InternalNote
                  }
                  isCannedAnswersLoaded={cannedAnswers.loaded}
                  onChange={(value) => {
                    setDraft({ reply_body: value });
                  }}
                  value={draft.reply_body}
                />
                <div className="reply-footer">
                  <div className="update-since">
                    <label>{t("LAST_SAVE")}</label>
                    <ConversationMessageReplyTimer t={t} since={draft.updated_at} />
                  </div>
                  <Button onClick={() => saveDraft()} className="draft">
                    {t("SAVE_REPLY")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
);
